package com.evoapps.evoshift.domain.budget

import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.plus
import kotlinx.serialization.Serializable

/*
 * SH-232: "Works without pay (0,00 zł)" with dates. Before SH-232 it was one undated list,
 * settings.payroll.unpaidUserIds (SH-147): a person on it worked unpaid on every day, past ones
 * included. Now each person has periods, settings.unpaidPeriods.{uid}: [{from, until}] (days,
 * inclusive; null from: from the start, null until: open-ended), shown and edited on the employee
 * timeline. The employee form's switch starts or ends a period from the first day of the current
 * month, as "Count in labour cost and SPMH" does (earlier months keep what they had).
 *
 * Older app versions read and write only unpaidUserIds, so it stays, and holds the people unpaid
 * today. settings.unpaidPeriodsAsOf is the day the list was last made to agree with the periods
 * (by the app's saves and the morning job, functions/unpaidWork.js). When the list differs from
 * what the periods said on that day, an older app changed it since: [UnpaidWork.reconcile] takes
 * that change from today on. Without any periods (all data from before) someone on the list is
 * unpaid from the start, as they always were. The same rule is in functions/unpaidWork.js; both
 * are tested against firebase/tests/golden/unpaid-work/cases.json.
 */

/** SH-232: the days [from]–[until] ("YYYY-MM-DD", inclusive) someone works without pay; null: from the start / open-ended. */
@Serializable
data class UnpaidPeriod(val from: String? = null, val until: String? = null) {
    fun covers(day: String): Boolean = (from == null || from <= day) && (until == null || day <= until)
}

object UnpaidWork {
    /** People with periods kept (a workplace's team, as the rules allow). */
    const val MAX_PEOPLE = 100

    /** Periods kept per person. */
    const val MAX_PERIODS = 24

    private fun date(text: String?): LocalDate? = text?.take(10)?.let { runCatching { LocalDate.parse(it) }.getOrNull() }
    private fun next(day: String): String = LocalDate.parse(day).plus(DatePeriod(days = 1)).toString()
    private fun previous(day: String): String = LocalDate.parse(day).minus(DatePeriod(days = 1)).toString()

    /** Whether [periods] cover [day] ("YYYY-MM-DD"). */
    fun covers(periods: List<UnpaidPeriod>, day: String): Boolean = periods.any { it.covers(day.take(10)) }

    /** [userId]'s periods in [payroll]: their own, or for someone only on the older list, from the start. */
    fun periodsOf(payroll: WorkplacePayrollSettings, userId: String): List<UnpaidPeriod> =
        payroll.unpaidPeriods[userId] ?: if (userId in payroll.unpaidUserIds) listOf(UnpaidPeriod()) else emptyList()

    /**
     * [periods] sorted (from the start first), well-formed (days that parse, an end not before
     * its start) and merged where they overlap or touch. At most [MAX_PERIODS], the latest kept.
     */
    fun normalized(periods: List<UnpaidPeriod>): List<UnpaidPeriod> {
        val valid = periods.mapNotNull { p ->
            val from = p.from?.let { date(it) ?: return@mapNotNull null }
            val until = p.until?.let { date(it) ?: return@mapNotNull null }
            if (from != null && until != null && until < from) null else UnpaidPeriod(from?.toString(), until?.toString())
        }.sortedWith(compareBy(nullsFirst()) { it.from })
        val merged = mutableListOf<UnpaidPeriod>()
        valid.forEach { p ->
            val last = merged.lastOrNull()
            // Touching: the next one starts the day after (or before) the last one's end.
            if (last != null && (last.until == null || p.from == null || p.from <= next(last.until))) {
                val until = if (last.until == null || p.until == null) null else maxOf(last.until, p.until)
                merged[merged.lastIndex] = last.copy(until = until)
            } else merged += p
        }
        return merged.takeLast(MAX_PERIODS)
    }

    /**
     * [periods] with [unpaid] from [from] on: whatever they said from that day is replaced
     * (earlier days keep what they had), then, when [unpaid], one open-ended period from [from].
     */
    fun withUnpaid(periods: List<UnpaidPeriod>, unpaid: Boolean, from: LocalDate): List<UnpaidPeriod> {
        val day = from.toString()
        val kept = periods.mapNotNull { p ->
            when {
                p.from != null && p.from >= day -> null
                p.until == null || p.until >= day -> p.copy(until = previous(day))
                else -> p
            }
        }
        return normalized(if (unpaid) kept + UnpaidPeriod(day, null) else kept)
    }

    /**
     * Everyone's periods in [payroll] with [userId]'s set to [periods] (none: they go). People
     * only on the older list get theirs from the start, so the stored periods say it all.
     */
    fun withPeriods(payroll: WorkplacePayrollSettings, userId: String, periods: List<UnpaidPeriod>): Map<String, List<UnpaidPeriod>> {
        val all = payroll.unpaidUserIds.associateWith { periodsOf(payroll, it) } + payroll.unpaidPeriods
        val updated = if (periods.isEmpty()) all - userId else all + (userId to normalized(periods))
        return updated.filterValues { it.isNotEmpty() }
    }

    /** The first day of [day]'s month: where the employee form's switch starts or ends a period. */
    fun monthStart(day: LocalDate): LocalDate = LocalDate(day.year, day.month, 1)

    /** Whether [periods] leave at least one day of [month] ("YYYY-MM") paid. */
    fun paidIn(periods: List<UnpaidPeriod>, month: String): Boolean {
        val first = date("$month-01") ?: return true
        val last = first.plus(DatePeriod(months = 1)).minus(DatePeriod(days = 1)).toString()
        val start = first.toString()
        // Normalized periods are merged: the month is unpaid only when one of them covers all of it.
        return normalized(periods).none { (it.from == null || it.from <= start) && (it.until == null || it.until >= last) }
    }

    /** "YYYY-MM" of [day]. */
    fun monthOf(day: LocalDate): String = "${day.year}-${day.month.number.toString().padStart(2, '0')}"

    /**
     * The older list (settings.payroll.unpaidUserIds) as [periods] say it on [today]: the people
     * unpaid today, plus [legacy] people without periods (unpaid from the start), sorted.
     */
    fun legacyOn(periods: Map<String, List<UnpaidPeriod>>, legacy: Collection<String>, today: String): List<String> =
        (periods.filterValues { covers(it, today) }.keys + legacy.filter { it !in periods }).distinct().sorted()

    /**
     * The periods as they stand on [today], with what an older app version changed in [legacy]
     * since [asOf] (the day the list last agreed with the periods) taken from today on:
     * - without [asOf] (nothing written since SH-232), someone on the list without periods is
     *   unpaid from the start;
     * - someone added to the list since, not unpaid today: unpaid from today, open-ended;
     * - someone removed from the list since, unpaid today: their period ends yesterday (one
     *   starting today goes).
     * Periods of people with none left are dropped.
     */
    fun reconcile(periods: Map<String, List<UnpaidPeriod>>, legacy: Collection<String>, asOf: String?, today: String): Map<String, List<UnpaidPeriod>> {
        val result = periods.mapValues { normalized(it.value) }.toMutableMap()
        val listed = legacy.toSet()
        (listed + result.keys).forEach { uid ->
            val own = result[uid].orEmpty()
            val inList = uid in listed
            if (asOf == null) {
                if (own.isEmpty() && inList) result[uid] = listOf(UnpaidPeriod())
                return@forEach
            }
            val agreed = covers(own, asOf)
            if (inList == agreed) return@forEach
            result[uid] = if (inList) {
                if (covers(own, today)) own else normalized(own + UnpaidPeriod(today, null))
            } else {
                own.mapNotNull { p ->
                    when {
                        !p.covers(today) -> p
                        p.from != null && p.from >= today -> null
                        else -> p.copy(until = previous(today))
                    }
                }
            }
        }
        return result.filterValues { it.isNotEmpty() }
    }
}
